




import { useState, useEffect } from "react";
import axios from "axios";
import toast from "react-hot-toast";
import { Upload, Search, Image as ImageIcon, Trash2 } from "lucide-react";import CreatableSelect from 'react-select/creatable';
import { API_BASE_URL } from "../../app/apiConfig";
import { convertImagesToWebP } from "../../utils/convertImageToWebP";
 
const IMAGE_BASE_URL = "https://uptask.visiontrek.io/uploads/gallery/";
 
export default function Gallery() {
  const [images, setImages] = useState([]);
  const [events, setEvents] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState("");
  const [deleteId, setDeleteId] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [files, setFiles] = useState([]);
  const [uploadEvent, setUploadEvent] = useState(null);
  const [loading, setLoading] = useState(false);
 
  // Fetch data
  const fetchData = async () => {
    try {
      const storedTheme = localStorage.getItem("theme");
      const eventQuery = selectedEvent ? `?event_name=${encodeURIComponent(selectedEvent)}` : "";
 
      const [imagesRes, eventsRes] = await Promise.all([
        axios.get(`${API_BASE_URL}/gallery${eventQuery}`, { withCredentials: true }),
        axios.get(`${API_BASE_URL}/gallery/events`, { withCredentials: true })
      ]);
 
      setImages(imagesRes.data);
 
      const eventOptions = eventsRes.data.map(e => ({ value: e, label: e }));
      setEvents(eventOptions);
    } catch (err) {
      toast.error("Failed to load gallery");
    }
  };
 
  useEffect(() => {
    fetchData();
  }, [selectedEvent]);
 
  // Upload
  const handleUpload = async () => {
    if (!files.length) return toast.error("Please select an image");
 
    setLoading(true);
    const formData = new FormData();
    const webpFiles = await convertImagesToWebP(files);
    webpFiles.forEach(f => formData.append("images", f));
    if (uploadEvent?.value) {
      formData.append("event_name", uploadEvent.value);
    }
 
    try {
      await axios.post(`${API_BASE_URL}/gallery/upload`, formData, {
        withCredentials: true,
        headers: { "Content-Type": "multipart/form-data" }
      });
      toast.success("Images uploaded successfully!");
      setModalOpen(false);
      setFiles([]);
      setUploadEvent(null);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.error || "Upload failed");
    } finally {
      setLoading(false);
    }
  };
 
  // Delete
const handleDelete = async () => {
  try {
    await axios.delete(`${API_BASE_URL}/gallery/${deleteId}`, { withCredentials: true });
    toast.success("Image deleted");
    setDeleteId(null);
    fetchData();
  } catch (err) {
    toast.error("Failed to delete image");
  }
};
 
  return (
    <div className=" text-white min-h-screen md:mt-20">
      {/* HEADER */}
      <div className="flex flex-col md:flex-row justify-between items-center mb-8 gap-4 bg-white/5 p-6 rounded-2xl border border-white/10 backdrop-blur-md shadow-2xl bg-gradient-to-br from-[#140c2b] via-[#1f1147] to-[#0f0825]">
        <div>
                <h1 className="text-3xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-purple-400 to-pink-600">Our Gallery</h1>
          <p className="text-white/60 text-sm mt-1">Discover moments from our events</p>
        </div>
 
        <div className="flex items-center gap-4 w-full md:w-auto">
          <div className="relative flex-1 md:w-64 text-black">
            <select
              className="w-full bg-white/10 text-white border border-white/20 rounded-lg px-4 py-2 appearance-none cursor-pointer hover:bg-white/20 transition"
              value={selectedEvent}
              onChange={(e) => setSelectedEvent(e.target.value)}
            >
              <option value="" className="text-black">All Events</option>
              {events.map((e, idx) => (
                <option key={idx} value={e.value} className="text-black">{e.label}</option>
              ))}
            </select>
          </div>
 
          <button
            onClick={() => setModalOpen(true)}
            className="flex items-center gap-2 bg-gradient-to-r from-purple-500 to-indigo-600 px-5 py-2 rounded-lg font-semibold hover:scale-105 transition shadow-lg"
          >
            <Upload size={18} />
            <span>Upload</span>
          </button>
        </div>
      </div>
 
      {/* MASONRY GALLERY */}
      {images.length === 0 ? (
        <div className="flex flex-col items-center justify-center p-20 text-white/40">
          <ImageIcon size={64} className="mb-4 opacity-50" />
          <p className="text-lg">No images found. Be the first to upload!</p>
        </div>
      ) : (
        <div className="columns-1 sm:columns-2 md:columns-3 lg:columns-4 gap-4 space-y-4">
          {images.map((img) => (
            <div key={img.id} className="relative group break-inside-avoid rounded-xl overflow-hidden shadow-xl border border-white/10 bg-black/40">
              <img
                src={`${IMAGE_BASE_URL}${img.image_url}`}
                alt="gallery"
                loading="lazy"
                className="w-full h-[250px] object-cover object-top transform group-hover:scale-105 transition duration-500"
                />
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                <div className="absolute bottom-0 left-0 right-0 p-4">
                  <div className="flex items-center gap-3">
                    {img.profile_pic ? (
                      <img
  src={`https://uptask.visiontrek.io${img.profile_pic}`}
  className="w-8 h-8 rounded-full border border-white/40"
/>                    ) : (
                      <div className="w-8 h-8 rounded-full bg-purple-500 flex items-center justify-center text-xs font-bold">
                        {img.first_name?.[0]}{img.last_name?.[0]}
                      </div>
                    )}
                    <div>
                      <p className="text-sm font-semibold">{img.first_name} {img.last_name}</p>
                      {img.event_name && <p className="text-xs text-purple-300">{img.event_name}</p>}
                    </div>
                  </div>
                  <button
                    // onClick={() => handleDelete(img.id)}
                    onClick={() => setDeleteId(img.id)}
                    className="absolute top-4 right-4 bg-red-500/80 p-1.5 rounded-full hover:bg-red-500 transition"
                  >
               <Trash2 size={14} />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
 
      {/* UPLOAD MODAL */}
      {modalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
          <div className="bg-[#291857] w-full max-w-md rounded-2xl shadow-2xl border border-white/20 p-6 relative">
            <button
              onClick={() => { setModalOpen(false); setFiles([]); setUploadEvent(null); }}
              className="absolute top-4 right-4 text-white/50 hover:text-white transition"
            >
              <Trash2 size={20} />
            </button>
 
            <h2 className="text-2xl font-bold mb-6 flex items-center gap-2">
              <Upload size={24} className="text-purple-400" /> Upload Images
            </h2>
 
            <div className="mb-4">
              <label className="block text-sm font-semibold mb-2 text-white/80">Event Name (Optional)</label>
              <div className="text-black">
                <CreatableSelect
                  isClearable
                  options={events}
                  value={uploadEvent}
                  onChange={(v) => setUploadEvent(v)}
                  placeholder="Select or type event..."
                  className="react-select-container"
                  classNamePrefix="react-select"
                />
              </div>
            </div>
 
            <div className="mb-6">
              <label className="block text-sm font-semibold mb-2 text-white/80">Select Images</label>
              <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed border-white/30 rounded-xl cursor-pointer hover:border-purple-400 hover:bg-white/5 transition">
                <div className="flex flex-col items-center justify-center pt-5 pb-6">
                  <ImageIcon size={32} className="mb-2 text-white/50" />
                  <p className="text-sm text-white/70">
                    <span className="font-semibold text-purple-300">Click to upload</span> or drag and drop
                  </p>
                </div>
                <input
                  type="file"
                  className="hidden"
                  multiple
                  accept="image/*"
                  onChange={(e) => setFiles(Array.from(e.target.files))}
                />
              </label>
 
              {files.length > 0 && (
                <div className="mt-3 text-sm text-purple-200">
                  {files.length} file(s) selected
                </div>
              )}
            </div>
 
            <button
              onClick={handleUpload}
              disabled={loading}
              className="w-full bg-gradient-to-r from-purple-500 to-indigo-600 text-white font-bold py-3 rounded-xl hover:shadow-lg hover:scale-[1.02] transition disabled:opacity-50"
            >
              {loading ? "Uploading..." : "Upload Now"}
            </button>
          </div>
        </div>
      )}








{deleteId && (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
    <div className="bg-[#1f1147] p-6 rounded-2xl shadow-2xl border border-white/20 w-[350px] text-center">
      
      <h3 className="text-lg font-semibold text-white mb-4">
        Delete Image?
      </h3>
      
      <p className="text-white/60 text-sm mb-6">
        Are you sure you want to delete this image?
      </p>

      <div className="flex justify-center gap-4">
        <button
          onClick={() => setDeleteId(null)}
          className="px-4 py-2 rounded-lg bg-white/10 text-white hover:bg-white/20"
        >
          Cancel
        </button>

        <button
          onClick={handleDelete}
          className="px-4 py-2 rounded-lg bg-red-500 text-white hover:bg-red-600"
        >
          Delete
        </button>
      </div>
    </div>
  </div>
)}
    </div>
  );
}
 



// import { useState, useEffect } from "react";
// import axios from "axios";
// import toast from "react-hot-toast";
// import { Upload, X, Search, Image as ImageIcon } from "lucide-react";
// import CreatableSelect from 'react-select/creatable';
 
// const IMAGE_BASE_URL = "https://uptask.visiontrek.io/uploads/gallery/";
 
// export default function Gallery() {

//   const [eventModalOpen, setEventModalOpen] = useState(false);
// const [newEventName, setNewEventName] = useState("");
//   const [images, setImages] = useState([]);
//   const [events, setEvents] = useState([]);
//   const [selectedEvent, setSelectedEvent] = useState("");
 
//   const [modalOpen, setModalOpen] = useState(false);
//   const [files, setFiles] = useState([]);
//   const [uploadEvent, setUploadEvent] = useState(null);
//   const [loading, setLoading] = useState(false);
 
//   // Fetch data
//   const fetchData = async () => {
//     try {
//       const storedTheme = localStorage.getItem("theme");
//       const eventQuery = selectedEvent ? `?event_name=${encodeURIComponent(selectedEvent)}` : "";
 
//       const [imagesRes, eventsRes] = await Promise.all([
//         axios.get(`/api/gallery${eventQuery}`, { withCredentials: true }),
//         axios.get(`/api/gallery/events`, { withCredentials: true })
//       ]);
 
//       // setImages(imagesRes.data);
//       setImages(imagesRes.data || []);
 
//       // const eventOptions = eventsRes.data.map(e => ({ value: e, label: e }));
//       const eventOptions = (eventsRes.data || []).map(e => ({
//         value: typeof e === "string" ? e : e.value || e.event_name,
//         label: typeof e === "string" ? e : e.label || e.event_name
//       }));
//       setEvents(eventOptions);


//       // ✅ KEEP selected event from disappearing
// if (uploadEvent) {
//   const exists = eventOptions.find(ev => ev.value === uploadEvent.value);
//   if (!exists) {
//     setUploadEvent({
//       value: uploadEvent.value,
//       label: uploadEvent.label
//     });
//   }
// }
//     } catch (err) {
//       toast.error("Failed to load gallery");
//     }
//   };
 
//   useEffect(() => {
//     fetchData();
//   }, [selectedEvent]);
 
//   // Upload
//   // const handleUpload = async () => {
//   //   if (!files.length) return toast.error("Please select an image");
 
//   //   setLoading(true);
//   //   const formData = new FormData();
//   //   files.forEach(f => formData.append("images", f));
//   //   if (uploadEvent?.value) {
//   //     formData.append("event_name", uploadEvent.value);
//   //   }
 
//   //   try {
//   //     await axios.post("/api/gallery/upload", formData, {
//   //       withCredentials: true,
//   //       headers: { "Content-Type": "multipart/form-data" }
//   //     });
//   //     toast.success("Images uploaded successfully!");
//   //     setModalOpen(false);
//   //     setFiles([]);
//   //     setUploadEvent(null);
//   //     fetchData();
//   //   } catch (err) {
//   //     toast.error(err.response?.data?.error || "Upload failed");
//   //   } finally {
//   //     setLoading(false);
//   //   }
//   // };

//   const handleUpload = async () => {
//     if (!files.length) return toast.error("Please select an image");
 
//     setLoading(true);
//     const formData = new FormData();
//     files.forEach(f => formData.append("images", f));
//     if (uploadEvent?.value) {
//       formData.append("event_name", uploadEvent.value);
//     }
 
//     try {
//       await axios.post("/api/gallery/upload", formData, {
//         withCredentials: true,
//         headers: { "Content-Type": "multipart/form-data" }
//       });
//       toast.success("Images uploaded successfully!");
//       setModalOpen(false);
//       setFiles([]);
//       setUploadEvent(null);
//       fetchData();
//     } catch (err) {
//       toast.error(err.response?.data?.error || "Upload failed");
//     } finally {
//       setLoading(false);
//     }
//   };
//   // Delete
//   const handleDelete = async (id) => {
//     if (!window.confirm("Are you sure?")) return;
//     try {
//       await axios.delete(`/api/gallery/${id}`, { withCredentials: true });
//       toast.success("Image deleted");
//       fetchData();
//     } catch (err) {
//       toast.error("Failed to delete image");
//     }
//   };




//   const handleAddEvent = async () => {
//     if (!newEventName.trim()) {
//       return toast.error("Enter event name");
//     }
  
//     try {
//       // 👉 If you have backend API, use this:
//       // await axios.post("/api/gallery/events", {
//       //   event_name: newEventName
//       // }, { withCredentials: true });
  
//       // ✅ TEMP (frontend only add)
//       const newEvent = {
//         value: newEventName,
//         label: newEventName
//       };
  
//       setEvents((prev) => [...prev, newEvent]);
//       setUploadEvent(newEvent); // auto select
//       setSelectedEvent(newEventName);
  
//       toast.success("Event added!");
  
//       setNewEventName("");
//       setEventModalOpen(false);
  
//     } catch (err) {
//       toast.error("Failed to add event");
//     }
//   };




 
//   return (
//     <div className="p-6 text-white min-h-screen md:mt-20 ">
//       {/* HEADER */}
//       <div className="flex flex-col md:flex-row justify-between items-center mb-8 gap-4 bg-white/5 p-6 rounded-2xl border border-white/10 backdrop-blur-md shadow-2xl bg-gradient-to-br from-[#140c2b] via-[#1f1147] to-[#0f0825]">
//         <div>
//           <h1 className="text-3xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-purple-400 to-pink-600">Our Gallery</h1>
//           <p className="text-white/60 text-sm mt-1">Discover moments from our events</p>
//         </div>
 
//         <div className="flex items-center gap-4 w-full md:w-auto">
//           <div className="relative flex-1 md:w-64 text-black">
//             <select
//               className="w-full bg-white/10 text-white border border-white/20 rounded-lg px-4 py-2 appearance-none cursor-pointer hover:bg-white/20 transition"
//               value={selectedEvent}
//               onChange={(e) => setSelectedEvent(e.target.value)}
//             >
//               <option value="" className="text-black">All Events</option>
//               {events.map((e, idx) => (
//                 <option key={idx} value={e.value} className="text-black">{e.label}</option>
//               ))}
//             </select>
//           </div>

//             {/* ✅ NEW BUTTON */}
//   <button
//     onClick={() => setEventModalOpen(true)}
//     className="bg-green-500 hover:bg-green-600 px-3 py-2 rounded-lg text-white font-bold"
//   >
//     + Event
//   </button>
 
//           <button
//             onClick={() => setModalOpen(true)}
//             className="flex items-center gap-2 bg-gradient-to-r from-purple-500 to-indigo-600 px-5 py-2 rounded-lg font-semibold hover:scale-105 transition shadow-lg"
//           >
//             <Upload size={18} />
//             <span>Add Photos</span>
//           </button>
//         </div>
//       </div>
 
//       {/* MASONRY GALLERY */}
//       {images.length === 0 ? (
//         <div className="flex flex-col items-center justify-center p-20 text-white/40">
//           <ImageIcon size={64} className="mb-4 opacity-50" />
//           <p className="text-lg">No images found. Be the first to upload!</p>
//         </div>
//       ) : (
//         // <div className="columns-1 sm:columns-2 md:columns-3 lg:columns-4 gap-4 space-y-4">
//         <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
//           {images.map((img) => (
//             <div key={img.id} className="relative group break-inside-avoid rounded-xl overflow-hidden shadow-xl border border-white/10 bg-black/40">
//               {/* <img
//                 src={`${IMAGE_BASE_URL}${img.image_url}`}
//                 alt="gallery"
//                 loading="lazy"
//                 className="w-full h-auto object-cover transform group-hover:scale-105 transition duration-500"
//               /> */}

// <img
//   src={`${IMAGE_BASE_URL}${img.image_url}`}
//   alt="gallery"
//   loading="lazy"
  // className="w-full h-[250px] object-cover object-top transform group-hover:scale-105 transition duration-500"
// />
//               <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
//                 <div className="absolute bottom-0 left-0 right-0 p-4">
//                   <div className="flex items-center gap-3">
//                     {img.profile_pic ? (
//                       <img src={`https://uptask.visiontrek.io/profile-uploads/${img.profile_pic}`} className="w-8 h-8 rounded-full border border-white/40" />
//                     ) : (
//                       <div className="w-8 h-8 rounded-full bg-purple-500 flex items-center justify-center text-xs font-bold">
//                         {img.first_name?.[0]}{img.last_name?.[0]}
//                       </div>
//                     )}
//                     <div>
//                       <p className="text-sm font-semibold">{img.first_name} {img.last_name}</p>
//                       {img.event_name && <p className="text-xs text-purple-300">{img.event_name}</p>}
//                     </div>
//                   </div>
//                   <button
//                     onClick={() => handleDelete(img.id)}
//                     className="absolute top-4 right-4 bg-red-500/80 p-1.5 rounded-full hover:bg-red-500 transition"
//                   >
//                     <X size={14} />
//                   </button>
//                 </div>
//               </div>
//             </div>
//           ))}
//         </div>
//       )}
 
//       {/* UPLOAD MODAL */}
//       {modalOpen && (
//         <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
//           <div className="bg-[#291857] w-full max-w-md rounded-2xl shadow-2xl border border-white/20 p-6 relative">
//             <button
//               onClick={() => { setModalOpen(false); setFiles([]); setUploadEvent(null); }}
//               className="absolute top-4 right-4 text-white/50 hover:text-white transition"
//             >
//               <X size={20} />
//             </button>
 
//             <h2 className="text-2xl font-bold mb-6 flex items-center gap-2">
//               <Upload size={24} className="text-purple-400" /> Upload Images
//             </h2>
 
//             <div className="mb-4">
//               <label className="block text-sm font-semibold mb-2 text-white/80">Event Name (Optional)</label>
//               <div className="text-black">
//                 {/* <CreatableSelect
//                   isClearable
//                   options={events}
//                   value={uploadEvent}
//                   onChange={(v) => setUploadEvent(v)}
//                   placeholder="Select or type event..."
//                   className="react-select-container"
//                   classNamePrefix="react-select"
//                 /> */}

// <CreatableSelect
//   isClearable
//   options={events}
//   value={uploadEvent}
//   onChange={(v) => setUploadEvent(v)}
//   onInputChange={(inputValue) => {
//     // 👇 THIS IS IMPORTANT
//     setUploadEvent({ value: inputValue, label: inputValue });
//   }}
//   placeholder="Select or type event..."
// />
//               </div>
//             </div>
 
//             <div className="mb-6">
//               <label className="block text-sm font-semibold mb-2 text-white/80">Select Images</label>
//               <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed border-white/30 rounded-xl cursor-pointer hover:border-purple-400 hover:bg-white/5 transition">
//                 <div className="flex flex-col items-center justify-center pt-5 pb-6">
//                   <ImageIcon size={32} className="mb-2 text-white/50" />
//                   <p className="text-sm text-white/70">
//                     <span className="font-semibold text-purple-300">Click to upload</span> or drag and drop
//                   </p>
//                 </div>
//                 <input
//                   type="file"
//                   className="hidden"
//                   multiple
//                   accept="image/*"
//                   onChange={(e) => setFiles(Array.from(e.target.files))}
//                 />
//               </label>
 
//               {files.length > 0 && (
//                 <div className="mt-3 text-sm text-purple-200">
//                   {files.length} file(s) selected
//                 </div>
//               )}
//             </div>
 
//             <button
//               onClick={handleUpload}
//               disabled={loading}
//               className="w-full bg-gradient-to-r from-purple-500 to-indigo-600 text-white font-bold py-3 rounded-xl hover:shadow-lg hover:scale-[1.02] transition disabled:opacity-50"
//             >
//               {loading ? "Uploading..." : "Upload Now"}
//             </button>
//           </div>
//         </div>
//       )}





// {eventModalOpen && (
//   <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
//     <div className="bg-[#291857] w-full max-w-md rounded-2xl shadow-2xl border border-white/20 p-6 relative">

//       <button
//         onClick={() => setEventModalOpen(false)}
//         className="absolute top-4 right-4 text-white/50 hover:text-white"
//       >
//         <X size={20} />
//       </button>

//       <h2 className="text-xl font-bold mb-4 text-white">
//         Add New Event
//       </h2>

//       <input
//         type="text"
//         value={newEventName}
//         onChange={(e) => setNewEventName(e.target.value)}
//         placeholder="Enter event name..."
//         className="w-full px-4 py-2 rounded-lg text-black mb-4"
//       />

//       <button
//         onClick={handleAddEvent}
//         className="w-full bg-green-500 hover:bg-green-600 text-white py-2 rounded-lg font-semibold"
//       >
//         Add Event
//       </button>
//     </div>
//   </div>
// )}
//     </div>
//   );
// }
 
